import { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';

/**
 * ClientAutocomplete — drop-in zamjena za Select za klijente.
 *
 * Pretraga po: skraceni_naziv, naziv_firme, PIB (in-memory).
 *
 * Props:
 * - clients: [] — lista klijenata (iz parent state-a, već učitana iz /api/clients)
 * - value: string (client.id) — trenutno odabrani klijent
 * - onChange: (client_id_or_empty, clientObj|null) => void
 * - placeholder: string
 * - disabled: boolean
 * - testid: string
 * - className: string (applied to Input)
 * - filter: (client) => boolean — optional dodatni filter (npr. samo aktivni)
 */
export default function ClientAutocomplete({
  clients = [],
  value = '',
  onChange,
  placeholder = 'Izaberite klijenta (naziv ili PIB)...',
  disabled = false,
  testid,
  className = 'bg-white border-slate-300 text-sm',
  filter,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Resolve selected client for display
  const selected = useMemo(
    () => clients.find(c => c.id === value) || null,
    [clients, value]
  );

  const displayValue = open
    ? query
    : (selected
        ? `${(selected.skraceni_naziv || selected.naziv_firme || '').trim()}${selected.pib ? ` (${selected.pib})` : ''}`
        : '');

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const results = useMemo(() => {
    const base = (filter ? clients.filter(filter) : clients);
    const sorted = [...base].sort((a, b) =>
      (a.skraceni_naziv || a.naziv_firme || '').localeCompare(b.skraceni_naziv || b.naziv_firme || '')
    );
    const q = (query || '').toLowerCase().trim();
    if (!q) return sorted.slice(0, 100);
    return sorted.filter(c =>
      (c.skraceni_naziv || '').toLowerCase().includes(q) ||
      (c.naziv_firme || '').toLowerCase().includes(q) ||
      (c.pib || '').toLowerCase().includes(q)
    ).slice(0, 100);
  }, [clients, query, filter]);

  const pick = (c) => {
    setOpen(false);
    setQuery('');
    onChange?.(c ? c.id : '', c || null);
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onChange?.('', null);
    setQuery('');
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={displayValue}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onClick={() => setOpen(true)}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        data-testid={testid}
        autoComplete="off"
      />
      {selected && !open && (
        <button
          type="button"
          onClick={clearSelection}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500 text-xs"
          title="Poništi izbor"
        >
          ✕
        </button>
      )}
      {open && !disabled && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
          {results.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">Nema rezultata</div>
          )}
          {results.map(c => (
            <button
              key={c.id}
              type="button"
              onMouseDown={e => { e.preventDefault(); pick(c); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-b-0 ${c.id === value ? 'bg-blue-50/70' : ''}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{c.skraceni_naziv || c.naziv_firme || '(bez naziva)'}</span>
                {c.pib && <span className="text-xs text-slate-500">PIB: {c.pib}</span>}
              </div>
              {c.naziv_firme && c.skraceni_naziv && c.naziv_firme !== c.skraceni_naziv && (
                <div className="text-[11px] text-slate-400 mt-0.5 truncate">{c.naziv_firme}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
